/*
 * 总结一下 Vuex 的使用经验
 * 1. Vuex 的 state 是响应式的，因此，如果界面上有使用到它的属性，那么一旦通过 state 修改了该元素，对应的界面（DOM节点）也会发生变化。
 *    目前，comment 的 likes/dislikes 使用到了该特性，从 commentMapCache 中找到对应的 comment 进行修改，结果会自动的作用到界面上。
 *
 */

const assert = require('assert').strict;
import Vue from 'vue'
import Vuex from 'vuex'
import {ShowEntityMode} from "../utils";

// 1.安装插件
Vue.use(Vuex)

// 2. 创建 Vue Store 对象
const store = new Vuex.Store({
    state: {
        title: '大自然', // H5 head 中间位置展示
        slogans: ['走过世界，欣赏这世界的美景，直到宇宙的尽头'],
        // v1: cache the signed in user info，注意有个原则，就是指 cache user 不变的部分，比如 username, email 等等。如果有变动的，比如积分等重要信息，则是不会缓存的。
        // v2: cache the anonymous user for anonymous purchase and the basic functions usage.
        user: null,
        newUserRewardPoints: 0, // 新用户注册并激活邮箱可获得积分
        channels: [], // cache the channels for global usage
        currentChannelCode: 'nature', // the current viewing channel. 只有在 Channel 频道切换后才更改。目前仅仅用在来黑博主后跳转到访问的 channel
        currentEntity: null, // the current popupingEntity that is displayed by popup popupingEntity, popupingEntity and comments components...
        displayMode: {  // 不同的入口页面对应的组件有不同的展示方式，这个属性就是用来控制展示方式的。
            showEntityMode: ShowEntityMode.SinglePageMode, // popuped or single entity page, default set to single page mode，所以，在需要 popup entity 显示的地方设置即可。
            isProfileNameNavigable: true, // 在 profile 页面中 popup entity window 中的 profile name 是不允许 navigate 的
            // 从用户中心的喜欢页面中，取消喜欢，需要确认，确认后，该喜欢的 entity 便直接被移除。
            unlike: {
                isNeedConfirm: false,
                confirmMsg: '是否从喜欢列表中移除？'
            },
            // 从用户中心的收藏页面中，取消收藏，需要确认，确认后，该收藏的 entity 便直接被移除。
            unfavorite: {
                isNeedConfirm: false,
                confirmMsg: '是否从收藏列表中移除？'
            }
        },
        i: null,   // is checking.... 是否在审核状态中。
        pId: null,  // HCF-434: 为了避免出现 xxpay 的字样，从后台获取 payment channel id
        displayCtrl: null, // 控制某些敏感元素的展示，对应后台配置的 frontend 控制属性
        // 教学
        tutorial: {
            profilePreview: {
                currentPreviews: 0, // 记录当前总共显示的次数
                maxPreviews: 3,     // 最大能够展示的次数
                preProfileCode: null    // 前一个提示的 profile，只有当不同的 profile 才会提示
            },
            wallpaperPreview: {
                currentPreviews: 0,
                maxPreviews: 3,  // 最多只能 preview 3 次
                preShortcode: null // 前一个 previewable 的 entity shortcode
            }
        },
        tipoffs: [], // 举报
        entityMapCache: new Map(), // the cached popupingEntity that the user get visted, for best hunt for performance...
        profileMapCache: new Map(),
        commentMapCache: new Map(), // key: shortcode, comments: Array
        channel: null, // the payment channel
        latestVersion: null
    },
    // 主要用来设值的。
    mutations: {

        /**
         *
         * @param state
         * @param channel, the payment channel
         */
        setChannel(state, channel){
            state.channel = channel;
            console.log(`store, setChannel /w channel ${channel}`);
        },

        setTitle(state, title){
            state.title = title;
            console.log(`store, setTitle /w title ${title}`);
        },

        setSlogans(state, slogans){
            state.slogans = slogans;
        },

        setTipoffs(state, tipoffs){
            state.tipoffs = tipoffs;
        },

        setCurrentChannelCode(state, channelCode){
            state.currentChannelCode = channelCode;
        },

        /**
         * 缓存页面当前所展示的 entity；很多子组件需要使用到 entity，比如 thread 等很深的子组件，这样方便它们直接获取到当前的 entity
         * @param state
         * @param entity
         * @param {String} from, "Single Page" or "List Page"
         */
        setCurrentEntity(state, entity) {
            assert(entity != null, 'the popupingEntity must not be Null');
            console.log('set the current popupingEntity into vuex store');
            state.currentEntity = entity;
        },

        /**
         * 缓存用户已经访问过的 entity，这个对于从 Single Entity Page 访问有用，如果再次访问，则无需从数据库中读取了。
         * @param state
         * @param entity
         */
        cacheEntity(state, entity){
            state.entityMapCache.set(entity.shortcode, entity);
        },

        setChannels(state, channels){
            state.channels = channels;
        },

        /**
         *
         * @param state
         */
        cacheComments(state, payload){
            state.commentMapCache.set(payload.shortcode, payload.comments);
        },

        /**
         * TODO rename to cacheProfile
         * @param state
         * @param {object} profile
         */
        setProfile(state, profile){
            state.profileMapCache.set(profile.code, profile);
        },

        setDisplayMode(state, mode){
            state.displayMode = mode;
        },

        setShowEntityMode(state, mode){
            state.displayMode.showEntityMode = mode;
        },

        setUnLikeConfirmMode(state, isNeedConfirm){
            state.displayMode.unlike.isNeedConfirm = isNeedConfirm;
        },

        setUnFavoriteConfirmMode(state, isNeedConfirm){
            state.displayMode.unfavorite.isNeedConfirm = isNeedConfirm;
        },

        setIsProfileNameNavigble(state, isNavigable){
            state.displayMode.isProfileNameNavigable = isNavigable;
        },

        setUser(state, user){
            state.user = user;
        },

        setNewUserRewardPoints(state, points){
            state.newUserRewardPoints = points;
        },

        changeUsername(state, username){
            if(state.user!==null){
                state.user.username = username;
            }else{
                console.warn('user in state is null')
            }
        },

        /**
         * add the entity shortcode into current user likes array
         * @param state
         * @param shortcode
         */
        addUserLikes(state, shortcode){
            if(state.user!==null){
                if(!state.user.likes.includes(shortcode)){
                    state.user.likes.unshift(shortcode); // 向数组头添加元素，因为最近的最先显示
                }
            }else{
                console.warn('user in state is null')
            }
        },

        removeUserLikes(state, shortcode){
            if(state.user!==null){
                if(state.user.likes.includes(shortcode)){
                    _.remove(state.user.likes, function(shortcode_){
                        return shortcode_ === shortcode;
                    })
                }
            }else{
                console.warn('user in state is null')
            }
        },

        addUserFavorites(state, shortcode){
            if(state.user!==null){
                if(!state.user.favorites.includes(shortcode)){
                    state.user.favorites.unshift(shortcode);
                }
            }else{
                console.warn('user in state is null')
            }
        },

        removeUserFavorites(state, shortcode){
            if(state.user!==null){
                if(state.user.favorites.includes(shortcode)){
                    _.remove(state.user.favorites, function(shortcode_){
                        return shortcode_ === shortcode;
                    })
                }
            }else{
                console.warn('user in state is null')
            }
        },

        /**
         * @param state
         * @param {String} code, the profile code
         */
        addFollows(state, code){
            if(state.user!==null){
                if(!state.user.follows.includes(code)){
                    state.user.follows.unshift(code);
                }
            }else{
                console.warn('user in state is null')
            }
        },

        /**
         * @param state
         * @param {String} code, the profile code
         */
        removeFollows(state, code){
            if(state.user!==null){
                if(state.user.follows.includes(code)){
                    _.remove(state.user.follows, function(code_){
                        return code_ === code;
                    })
                }
            }else{
                console.warn('user in state is null')
            }
        },

        setWallpaperPreviewShortcode(state, shortcode){
            state.tutorial.wallpaperPreview.preShortcode = shortcode;
        },

        incWallpaperCurrentPreviews(state){
            if (state.tutorial.wallpaperPreview.currentPreviews < state.tutorial.wallpaperPreview.maxPreviews){
                state.tutorial.wallpaperPreview.currentPreviews ++;
            }else{
                console.warn(`wallpaper preview, currentPreviews ${state.tutorial.wallpaperPreview.currentPreviews} is bigger than maxPreviews, ignore this increasse`);
            }
        },

        setTutorialPreProfileCode(state, profileCode){
            state.tutorial.profilePreview.preProfileCode = profileCode;
        },

        incTutorialProfilePreviews(state){
            if (state.tutorial.profilePreview.currentPreviews < state.tutorial.profilePreview.maxPreviews){
                state.tutorial.profilePreview.currentPreviews ++;
            }else{
                console.warn(`wallpaper preview, currentPreviews ${state.tutorial.profilePreview.currentPreviews} is bigger than maxPreviews, ignore this increasse`);
            }
        },

        setDisplayCtrl(state, displayCtrl){
            state.displayCtrl = displayCtrl;
        },

        setI(state, i){
            state.i = i;
        },

        setPid(state, pId){
            state.pId = pId;
        },

        // 同步 profile code 的 blacklist
        updateBlacklist(state, type, profileCode){
            if (type === 'profile')
                state.user.blackList.profileCodes.append(profileCode);
        },

        setLatestVersion(state, version) {
            state.latestVersion = version;
        }

    },
    actions: {},
    getters: {
        popTitle(state){
            return state.titles.pop();  // 弹出队列中的最后一个元素
        },
        getChannel(state){
            return function(code){
                for (let c of state.channels){
                    if( c.code === code )
                        return c.name
                }
                throw new Error(`can not found channel name from ${code}`);
            }
        },
        /**
         *
         * @param state
         * @return {function(*=): unknown}, if not found, will return undefined
         */
        getCacheEntity(state){
            return function(shortcode){
                var e = state.entityMapCache.get(shortcode);
                return e;
            }
        },
        getProfile(state){
            /**
             * @param {String} code, the profile code
             */
            return function(code){
                var p = state.profileMapCache.get(code);
                return p;
            }
        },
        getDisplayMode(state){
            return function(){
                return state.displayMode;
            }
        },
        getShowEntityMode(state){
            return function(){
                return state.displayMode.showEntityMode;
            }
        },
        getIsProfileNameNavigble(state){
            return function(){
                return state.displayMode.isProfileNameNavigable;
            }
        },
        getUser(state){
            return function(){
                return state.user;
            }
        },
        getComments(state){
            return function(shortcode){
                return state.commentMapCache.get(shortcode);
            }
        },
        /**
         * 两种情况不能 show
         * 1. 如果两次都是同一个 Entity，第二次不能 show
         * 2. 如果超过了全局最大的展示次数，不能 show
         * @param state
         * @return {boolean}
         */
        isWallpaperTootipShowable(state){
            return function(shortcode){
                let preShortcode = state.tutorial.wallpaperPreview.preShortcode;
                if(preShortcode !== shortcode){  // 表示是一个新的 Entity，则可以再次展示
                    if(state.tutorial.wallpaperPreview.currentPreviews < state.tutorial.wallpaperPreview.maxPreviews){
                        return true;
                    }
                }
                return false;
            }
        }
    },
    modules: {}
})

// 3.导出 store
export default store



// WEBPACK FOOTER //
// ./src/store/index.js